import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Pencil, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { SIZE_CLASSES } from '@wrapforce/shared';
import type { SizeClass, VehiclesPanelRow, WorkspaceVehicleInput } from '@wrapforce/shared';
import type { VehicleRefSource } from '@wrapforce/shared/schemas/pricing';
import { useDebounce } from '@/lib/useDebounce';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { useConfirm } from '@/components/ui/confirm-dialog';
import {
  SIZE_CLASS_DEFAULTS, SIZE_CLASS_LABEL_KEYS, DIFFICULTY_PRESETS, estimateDisassemblyMinutes,
  type DifficultyPresetKey,
} from '@/features/quotes/pricing/VehiclePicker';
import { useCreateWorkspaceVehicle } from '@/features/quotes/pricing/pricingApi';
import { useDeleteVehicleDisassemblyOverride, useVehiclesPanel } from './api';
import { VehicleDetailScreen } from './VehicleDetailScreen';

const PAGE_SIZES = [25, 50, 100] as const;

const SOURCE_BADGE_STYLE: Record<VehiclesPanelRow['source'], string> = {
  catalog: 'bg-surface-2 text-muted',
  custom: 'bg-accent/15 text-accent-ink',
  overridden: 'bg-warning/15 text-warning',
};

// source→VehicleRefSource mapping: catalog/overridden rows live in the
// platform catalog vehicle id space, custom rows live in the workspace
// vehicle id space (VehiclesPanelRow.source 'custom' -> 'workspace').
function toVehicleRefSource(source: VehiclesPanelRow['source']): VehicleRefSource {
  return source === 'custom' ? 'workspace' : 'catalog';
}

export function VehiclesTab({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('pricingV2');
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialog } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [sizeClass, setSizeClass] = useState<SizeClass | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZES)[number]>(25);
  const [addOpen, setAddOpen] = useState(false);

  const selectedVehicleId = searchParams.get('vehicleId');
  const selectedVehicleSource = searchParams.get('vehicleSource') as VehicleRefSource | null;

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sizeClass, pageSize]);

  const query = {
    page, pageSize, q: debouncedSearch.trim() || undefined, sizeClass: sizeClass || undefined,
  };
  const { data, isFetching } = useVehiclesPanel(workspaceId, query);

  const deleteOverride = useDeleteVehicleDisassemblyOverride(workspaceId);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  function invalidatePanel() {
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'vehicles-panel'] });
  }

  function openDetail(row: VehiclesPanelRow) {
    const next = new URLSearchParams(searchParams);
    next.set('vehicleId', row.id);
    next.set('vehicleSource', toVehicleRefSource(row.source));
    setSearchParams(next, { replace: false });
  }

  function closeDetail() {
    const next = new URLSearchParams(searchParams);
    next.delete('vehicleId');
    next.delete('vehicleSource');
    setSearchParams(next, { replace: false });
  }

  async function handleResetOverride(row: VehiclesPanelRow) {
    const confirmed = await confirm({
      title: t('vehicles.resetConfirmTitle'),
      description: t('vehicles.resetConfirmBody'),
      confirmLabel: t('vehicles.resetConfirmAction'),
    });
    if (!confirmed) return false;

    try {
      await deleteOverride.mutateAsync(row.id);
      invalidatePanel();
      return true;
    } catch {
      toast.error(t('vehicles.saveError'));
      return false;
    }
  }

  if (selectedVehicleId && selectedVehicleSource) {
    const selectedRow = data?.rows.find((r) => r.id === selectedVehicleId) ?? null;
    return (
      <VehicleDetailScreen
        workspaceId={workspaceId}
        vehicleId={selectedVehicleId}
        source={selectedVehicleSource}
        row={selectedRow}
        onBack={closeDetail}
        onResetOverride={handleResetOverride}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-wrap gap-3">
          <CardTitle>
            {data ? t('vehicles.countSummary', { count: data.total, customCount: data.customCount }) : t('vehicles.title')}
          </CardTitle>
          <Button size="sm" variant="secondary" className="ml-auto gap-1.5" onClick={() => setAddOpen(true)}>
            <Plus size={13} /> {t('vehicles.add')}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder={t('vehicles.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <select
              value={sizeClass}
              onChange={(e) => setSizeClass(e.target.value as SizeClass | '')}
              className="h-10 border border-border bg-surface px-3 text-sm"
            >
              <option value="">{t('vehicles.allBodyClasses')}</option>
              {SIZE_CLASSES.map((sc) => (
                <option key={sc} value={sc}>{t(SIZE_CLASS_LABEL_KEYS[sc], { ns: 'quotes' })}</option>
              ))}
            </select>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value) as (typeof PAGE_SIZES)[number])}
              className="ml-auto h-10 border border-border bg-surface px-3 text-sm"
            >
              {PAGE_SIZES.map((ps) => (
                <option key={ps} value={ps}>{t('vehicles.perPage', { count: ps })}</option>
              ))}
            </select>
          </div>

          <div className="overflow-hidden border border-border">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2 text-left text-[10px] font-bold uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-3 py-2">{t('vehicles.table.vehicle')}</th>
                  <th className="px-3 py-2">{t('vehicles.table.bodyClass')}</th>
                  <th className="px-3 py-2">{t('vehicles.table.l1')}</th>
                  <th className="px-3 py-2">{t('vehicles.table.l2')}</th>
                  <th className="px-3 py-2">{t('vehicles.table.l3')}</th>
                  <th className="px-3 py-2">{t('vehicles.table.source')}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows ?? []).map((row) => (
                  <tr key={row.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-ink">{row.make} {row.model}</div>
                      {row.trimName ? <div className="text-xs text-muted">{row.trimName}</div> : null}
                    </td>
                    <td className="px-3 py-2 text-muted">{t(SIZE_CLASS_LABEL_KEYS[row.sizeClass], { ns: 'quotes' })}</td>
                    <td className="px-3 py-2 font-mono">{t('vehicles.hoursValue', { hours: (row.l1Minutes / 60).toFixed(1) })}</td>
                    <td className="px-3 py-2 font-mono">{t('vehicles.hoursValue', { hours: (row.l2Minutes / 60).toFixed(1) })}</td>
                    <td className="px-3 py-2 font-mono">{t('vehicles.hoursValue', { hours: (row.l3Minutes / 60).toFixed(1) })}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${SOURCE_BADGE_STYLE[row.source]}`}>
                        {t(`vehicles.source.${row.source}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-3">
                        {row.source === 'overridden' ? (
                          <button
                            type="button"
                            onClick={() => handleResetOverride(row)}
                            className="text-xs font-semibold text-muted hover:text-ink"
                          >
                            {t('vehicles.resetToDefault')}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => openDetail(row)}
                          aria-label={t('vehicles.edit')}
                          className="text-muted hover:text-ink"
                        >
                          <Pencil size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data && data.rows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted">{t('vehicles.empty')}</div>
            ) : null}
          </div>

          <div className="flex items-center justify-between text-xs text-muted">
            <span>{isFetching ? t('vehicles.loading') : t('vehicles.pageOf', { page, totalPages })}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                {t('vehicles.prevPage')}
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                {t('vehicles.nextPage')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {addOpen ? (
        <AddVehicleDialog
          workspaceId={workspaceId}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            invalidatePanel();
          }}
        />
      ) : null}

      <ConfirmDialog />
    </div>
  );
}

// Hours-based numeric field (quarter-hour steps) - converts to/from minutes at
// the boundary, matching WrapTab.tsx's own HoursField for the shop-wide
// L1/L2/L3 defaults (sibling, not shared, per D-34-05's own "settings-only
// forms don't reuse quote-building components" rationale). Also used by
// VehicleDetailScreen's disassembly section.
export function HoursField({
  id, label, minutes, onChange,
}: {
  id: string;
  label: string;
  minutes: number;
  onChange: (minutes: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        max={100}
        step={0.25}
        value={minutes / 60}
        onChange={(e) => onChange(Math.max(0, Math.round(Number(e.target.value) * 60)))}
      />
    </div>
  );
}

// Sibling to VehiclePicker's CustomVehicleForm (quotes/pricing/VehiclePicker.tsx):
// same size-class defaulting so a shop never has to guess a raw ft/minutes/
// complexity number, but here L1/L2/L3 disassembly hours are shown as
// required, directly editable fields rather than a silent auto-estimate -
// this is the settings-side vehicle management surface, not a mid-quote
// shortcut (D-34-05).
function AddVehicleDialog({
  workspaceId, onClose, onCreated,
}: {
  workspaceId: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation('pricingV2');
  const createVehicle = useCreateWorkspaceVehicle(workspaceId);
  const [label, setLabel] = useState('');
  const [sizeClass, setSizeClass] = useState<SizeClass>('sedan');
  const [baseFtX10, setBaseFtX10] = useState(SIZE_CLASS_DEFAULTS.sedan.baseFtX10);
  const [baseMinutes, setBaseMinutes] = useState(SIZE_CLASS_DEFAULTS.sedan.baseMinutes);
  const [difficultyPreset, setDifficultyPreset] = useState<DifficultyPresetKey>('average');
  const complexityBp = Math.round(SIZE_CLASS_DEFAULTS[sizeClass].complexityBp * DIFFICULTY_PRESETS.find((p) => p.key === difficultyPreset)!.multiplier);
  const estimate = estimateDisassemblyMinutes(complexityBp);
  const [l1Minutes, setL1Minutes] = useState(estimate.l1DisassemblyMinutes);
  const [l2Minutes, setL2Minutes] = useState(estimate.l2DisassemblyMinutes);
  const [l3Minutes, setL3Minutes] = useState(estimate.l3DisassemblyMinutes);
  const [minutesTouched, setMinutesTouched] = useState(false);

  function handleSizeClassChange(next: SizeClass) {
    setSizeClass(next);
    const defaults = SIZE_CLASS_DEFAULTS[next];
    setBaseFtX10(defaults.baseFtX10);
    setBaseMinutes(defaults.baseMinutes);
    setDifficultyPreset('average');
    if (!minutesTouched) {
      const next2 = estimateDisassemblyMinutes(defaults.complexityBp);
      setL1Minutes(next2.l1DisassemblyMinutes);
      setL2Minutes(next2.l2DisassemblyMinutes);
      setL3Minutes(next2.l3DisassemblyMinutes);
    }
  }

  async function handleCreate() {
    if (!workspaceId || label.trim() === '') return;
    const input: WorkspaceVehicleInput = {
      label: label.trim(),
      sizeClass,
      baseFtX10,
      baseMinutes,
      complexityBp,
      isCustom: true,
      l1DisassemblyMinutes: l1Minutes,
      l2DisassemblyMinutes: l2Minutes,
      l3DisassemblyMinutes: l3Minutes,
    };
    try {
      await createVehicle.mutateAsync(input);
      toast.success(t('vehicles.addedToast'));
      onCreated();
    } catch {
      toast.error(t('vehicles.saveError'));
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} title={t('vehicles.addDialogTitle')}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="av-label">{t('vehicles.form.label')}</Label>
          <Input id="av-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('vehicles.form.labelPlaceholder')} />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="av-size">{t('vehicles.form.bodyClass')}</Label>
          <select
            id="av-size"
            value={sizeClass}
            onChange={(e) => handleSizeClassChange(e.target.value as SizeClass)}
            className="h-9 border border-border bg-surface px-2 text-sm"
          >
            {SIZE_CLASSES.map((sc) => (
              <option key={sc} value={sc}>{t(SIZE_CLASS_LABEL_KEYS[sc], { ns: 'quotes' })}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-3 gap-3 border-t border-border pt-3">
          <HoursField
            id="av-l1"
            label={t('vehicles.form.l1Hours')}
            minutes={l1Minutes}
            onChange={(m) => { setMinutesTouched(true); setL1Minutes(m); }}
          />
          <HoursField
            id="av-l2"
            label={t('vehicles.form.l2Hours')}
            minutes={l2Minutes}
            onChange={(m) => { setMinutesTouched(true); setL2Minutes(m); }}
          />
          <HoursField
            id="av-l3"
            label={t('vehicles.form.l3Hours')}
            minutes={l3Minutes}
            onChange={(m) => { setMinutesTouched(true); setL3Minutes(m); }}
          />
        </div>
        <p className="text-xs text-muted">{t('vehicles.form.minutesHint')}</p>

        <Button type="button" loading={createVehicle.isPending} disabled={label.trim() === ''} onClick={handleCreate}>
          {t('vehicles.form.save')}
        </Button>
      </div>
    </Dialog>
  );
}

